import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, AlertCircle, Ship, Package, Anchor, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

// ============================================================================
// PRODUCTION TRACKING DASHBOARD - On the Water & Booking Stage
// ============================================================================
export default function ProductionTrackingDashboard() {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onWaterData, setOnWaterData] = useState({ summary: [], totals: {}, filters: {} });

  // Filter state
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [groupBy, setGroupBy] = useState('customer');

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0';
    const num = Number(value) || 0;
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getProductionOnWater = httpsCallable(functions, 'getProductionOnWater', { timeout: 150000 });

      const result = await getProductionOnWater({
        season: selectedSeason,
        division: selectedDivision,
        customer: selectedCustomer,
        groupBy: groupBy
      });

      setOnWaterData(result.data);
    } catch (err) {
      console.error('Error fetching production tracking data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, selectedDivision, selectedCustomer, groupBy]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear filters
  const clearFilters = () => {
    setSelectedSeason(null);
    setSelectedDivision(null);
    setSelectedCustomer(null);
  };

  const hasActiveFilters = selectedSeason || selectedDivision || selectedCustomer;

  // Export to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const exportData = onWaterData.summary.map(row => ({
      'Group': row.group_name || row.group_label || '-',
      'PO Count': row.po_count,
      'Containers': row.container_count,
      'Units': row.total_units,
      'FOB Cost': row.total_fob_cost,
      'Landed Cost': row.total_landed_cost,
      'Sales': row.total_sales,
      'GP': row.total_gp,
      'Earliest Ship': row.earliest_ship_date,
      'Latest Ship': row.latest_ship_date
    }));

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportData), 'On Water');
    XLSX.writeFile(wb, `Production_OnWater_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const { summary, totals, filters } = onWaterData;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
            <Ship size={20} className="text-cyan-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Production Tracking</h2>
            <p className="text-xs text-slate-500">Track production on the water and booking stage</p>
          </div>
        </div>
      </div>

      {/* Totals Summary Cards */}
      {!loading && !error && totals && (
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-white rounded-lg border border-slate-200 p-4 text-center shadow-sm">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Package size={14} className="text-blue-500" />
              <p className="text-xs text-slate-500 font-medium">PO Count</p>
            </div>
            <p className="text-xl font-bold text-slate-800">{formatNumber(totals.po_count)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 text-center shadow-sm">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Anchor size={14} className="text-cyan-500" />
              <p className="text-xs text-slate-500 font-medium">Containers</p>
            </div>
            <p className="text-xl font-bold text-slate-800">{formatNumber(totals.container_count)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 text-center shadow-sm">
            <p className="text-xs text-slate-500 font-medium mb-1">Total Units</p>
            <p className="text-xl font-bold text-slate-800">{formatNumber(totals.total_units)}</p>
          </div>
          <div className="bg-cyan-50 rounded-lg border border-cyan-200 p-4 text-center shadow-sm">
            <p className="text-xs text-cyan-600 font-medium mb-1">Total FOB Cost</p>
            <p className="text-xl font-bold text-cyan-700">{formatCurrency(totals.total_fob_cost)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 text-center shadow-sm">
            <p className="text-xs text-blue-600 font-medium mb-1">Total Landed Cost</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(totals.total_landed_cost)}</p>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Group By */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Group By:</span>
              {['customer', 'division', 'factory', 'season', 'container'].map((g) => (
                <button
                  key={g}
                  onClick={() => setGroupBy(g)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors capitalize ${
                    groupBy === g
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>

            {/* Season Filter */}
            {filters.seasons && filters.seasons.length > 0 && (
              <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
                <span className="text-xs font-medium text-slate-500">Season:</span>
                <select
                  value={selectedSeason || ''}
                  onChange={(e) => setSelectedSeason(e.target.value || null)}
                  className="text-xs border border-slate-200 rounded px-2 py-1"
                >
                  <option value="">All</option>
                  {filters.seasons.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Division Filter */}
            {filters.divisions && filters.divisions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-slate-500">Division:</span>
                <select
                  value={selectedDivision || ''}
                  onChange={(e) => setSelectedDivision(e.target.value || null)}
                  className="text-xs border border-slate-200 rounded px-2 py-1"
                >
                  <option value="">All</option>
                  {filters.divisions.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
                Reset
              </button>
            )}
            <button onClick={handleExportExcel} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
              <Download size={12} /> Excel
            </button>
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700 disabled:opacity-50">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* On the Water Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-white flex items-center gap-2">
          <Ship size={16} className="text-cyan-600" />
          <h3 className="text-sm font-semibold text-slate-800">On the Water (Shipped)</h3>
          <span className="text-xs text-slate-400">Stage = Shipped</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={20} className="animate-spin text-cyan-500" />
            <span className="ml-2 text-sm text-slate-600">Loading data...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <span className="text-sm text-red-600">{error}</span>
          </div>
        ) : summary.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <span className="text-sm text-slate-500">No shipped production found</span>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 capitalize">{groupBy}</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">POs</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Containers</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Units</th>
                  <th className="px-2 py-2 text-right font-semibold text-cyan-600 border-b border-slate-200 bg-cyan-50/50">FOB Cost</th>
                  <th className="px-2 py-2 text-right font-semibold text-blue-600 border-b border-slate-200 bg-blue-50/50">Landed Cost</th>
                  <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">Sales</th>
                  <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">GP</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Ship Date Range</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2 font-medium text-slate-700">{row.group_name || row.group_label || '-'}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatNumber(row.po_count)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatNumber(row.container_count)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatNumber(row.total_units)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium text-cyan-700">{formatCurrency(row.total_fob_cost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium text-blue-700">{formatCurrency(row.total_landed_cost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-green-600">{formatCurrency(row.total_sales)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-purple-600">{formatCurrency(row.total_gp)}</td>
                    <td className="px-2 py-2 text-right text-slate-500 text-[10px]">
                      {formatDate(row.earliest_ship_date)} - {formatDate(row.latest_ship_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-semibold text-slate-700">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatNumber(totals.po_count)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatNumber(totals.container_count)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatNumber(totals.total_units)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-cyan-700">{formatCurrency(totals.total_fob_cost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-blue-700">{formatCurrency(totals.total_landed_cost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-green-600">{formatCurrency(totals.total_sales)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-purple-600">{formatCurrency(totals.total_gp)}</td>
                  <td className="px-2 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Placeholder for Booking Stage */}
      <div className="bg-white rounded-lg border border-dashed border-slate-300 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white flex items-center gap-2">
          <Package size={16} className="text-amber-600" />
          <h3 className="text-sm font-semibold text-slate-800">Booking Stage (Coming Soon)</h3>
          <span className="text-xs text-slate-400">Production about to ship</span>
        </div>
        <div className="flex items-center justify-center py-12 text-slate-400">
          <p className="text-sm">Booking stage tracking will be added here</p>
        </div>
      </div>
    </div>
  );
}
